import { commonHeaders } from "../ClientResources";
import { VcdEncryptedModel } from "../Vcd/EncryptedModel";
import { ProcessResponse, ProcessVcdResponse, VcdErrorDetail, VcdError, VcdBase } from "../Vcd/Misc";
import { deviceLinkLocalhostRoot, ApiDevices, Device } from "./Devices";
import { NIL as NIL_UUID } from 'uuid';
import { CalibrationCreation, DeviceAuditTrailCreation, DiaryComplianceCreation, FvcSessionCreation, SubjectCreation } from "webportalclient";
import { DiarySessionCreation } from "webportalclient";
import { AlertCreation } from "webportalclient";
import { DeviceTypes } from "../apiTypes";
import { FailedToConnectAfterRestartMessage } from "./P230In2itiveMessages";
import { FvcAnalysisSetCreation } from "webportalclient";
import { FvcAnalysisSetTest } from "webportalclient";

interface In2itiveError extends VcdBase {
    error: VcdErrorDetail;
}

export function isIn2itiveError(
    model: any
): model is In2itiveError {
    if (model === undefined || model === null || model.error === undefined) return false;
    return (model as In2itiveError).error !== undefined;
}

export const ConfigurationUpdate = (deviceId: string, vcdConfiguration: any): Promise<any> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/ConfigurationUpdate`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(vcdConfiguration),
    })
        .then(ProcessVcdResponse)
}

export const DeviceIdentification = (deviceId: string): Promise<any> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/DeviceIdentification`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(ProcessVcdResponse)
}

export const ConfigureDateTime = (deviceId: string, dateTime: string): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/SetTime`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({
            DateTime: dateTime,
        }),
    })
        .then(response => response.ok)
}

interface SubjectCreationWrapper {
    subject: SubjectCreation;
}

export const GetSubject = (deviceId: string): Promise<SubjectCreationWrapper | void> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetSubject`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(ProcessResponse)
}

export interface DiarySessionCollection {
    eDiarySessions: DiarySessionCreation[];
}

export const GetSession = (deviceId: string): Promise<DiarySessionCollection | In2itiveError> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetSession`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(ProcessResponse)
}

export interface DiaryAlertsCollection {
    alerts: AlertCreation[];
}

export const GetAlerts = (deviceId: string): Promise<DiaryAlertsCollection | In2itiveError> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetAlerts`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(ProcessResponse)
}

export interface DiaryComplianceCollection {
    compliances: DiaryComplianceCreation[];
}

export const GetCompliance = (deviceId: string): Promise<DiaryComplianceCollection | In2itiveError> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetCompliance`, {
        method: 'POST',
        ...commonHeaders,
    })
          .then(ProcessResponse)
}

export const GetAudit = (deviceId: string, lastRecordID: string = NIL_UUID): Promise<DeviceAuditTrailCreation[]> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetAudit`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({
            LastRecordID: lastRecordID,
        })
    })
        .then(response => response.json())
}

export const GetCalibration = (deviceId: string): Promise<CalibrationCreation> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetAccuracy`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => response.json())
}

export const ClearMemory = (deviceId: string): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/ClearMemory`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => response.ok)
}


export const Restart = (deviceId: string): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/Restart`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => response.ok)
}

export const RestartWithWait = (deviceId: string): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/RestartWithWait`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => response.ok)
}

export const ApplicationUpdate = async (deviceId: string, applicationPackage: string): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/ApplicationUpdate`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({
            package: applicationPackage
        }),
    })
        .then(response => response.ok)
}

export const RemoteMode = (deviceId: string): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/RemoteMode`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => response.ok)
}

export const ReConnectToDevice = async (): Promise<Device> => {
    let successfullyReconnected = false;
    let retries = 4;
    let device: Device;
    while (retries > 0 && !successfullyReconnected) {
        const devices = await ApiDevices();
        device = devices.find(d => d.type === DeviceTypes.In2itiveP230EDiary);

        if (device !== null && device !== undefined) {
            successfullyReconnected = true;
            break;
        }

        await new Promise(resolve => setTimeout(resolve, 5000));
        retries--;
    }

    if (!successfullyReconnected)
        throw new Error(FailedToConnectAfterRestartMessage);

    return device;
}

export interface FvcSessionCollection {
    fvcSessions: FvcSessionCreation[];
}

export const GetHomeSpirometrySessions = (deviceId: string): Promise<FvcSessionCollection> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetHomeSpirometrySessions`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => {
            if (response.status === 204) {
                return { fvcSessions: [] }
            } else {
                return response.json()
            }
        })
}

export interface SpirometryAnalysisSetCollection {
    fvcAnalysisSet: FvcAnalysisSetCreation[];
}

export const GetHomeSpirometrySessionAnalysis = (deviceId: string): Promise<SpirometryAnalysisSetCollection> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetHomeSpirometrySessionAnalysis`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => {
            if (response.status === 204) {
                return { fvcAnalysisSet: [] }
            } else {
                return response.json()
            }
        })
}

export interface SpirometryAnalysisSetTestCollection {
    fvcAnalysisSetTest: FvcAnalysisSetTest[];
}

export const GetHomeSpirometryTestAnalysis = (deviceId: string): Promise<SpirometryAnalysisSetTestCollection> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetHomeSpirometryTestAnalysis`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => {
            if (response.status === 204) {
                return { fvcAnalysisSetTest: [] }
            } else {
                return response.json()
            }
        })
}
